import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  getLoansBySearch,
  getLoanSearchFilters,
} from "../../actions/loan.action";
import Badge from "../../components/badge";
import { SearchBox } from "../../components/input-functions";
import MultiSelect, {
  convertFilterArrayToObject,
  convertFilterObjectToArray,
  MultiSelectOption,
} from "../../components/multi-select";
import Pagination from "../../components/pagination";
import Table, { TableColumns } from "../../components/table";
import Tabs, { TabItem } from "../../components/tabs";
import {
  CommercialLoanDetails,
  RetailODDetails,
  TermLoanDetails,
} from "../../dto";
import useQueryParams from "../../hooks/useQueryParams";
import useScreenName from "../../hooks/useScreenName";
import dispatch from "../../middleware";
import {
  LoanStatusEnum,
  LoanType,
  RoutePaths,
  SearchFilter,
} from "../../models";
import { ReduxState } from "../../reducers";
import {
  clearFormData,
  dateToDDMMYYYY,
  isLoadingActive,
  splitKeyWithSpace,
} from "../../utils";

const tabItems: TabItem<LoanType>[] = [
  {
    label: "Term Loan",
    value: "TERM_LOAN",
  },
  {
    label: "Earned Wage Access",
    value: "RETAIL_OD",
  },
];

function getColumns(loanType: LoanType): TableColumns[] {
  return [
    { field: "_id", label: "Loan ID" },
    { field: "appId", label: "Application ID" },
    { field: "userName", label: "User Name" },
    // INFO: Temporarily hide employer name column on the loans list view in LMS Portal
    // For More details refer: https://refynetech.atlassian.net/browse/BANKING-286
    // { field: "employerName", label: "Employer Name" },
    { field: "createdAt", label: "Date Of Loan" },
    // { field: "irpa", label: "IRPA (%)" },
    // { field: "pf", label: "PF" },
    ...(loanType === "TERM_LOAN"
      ? [
          {
            field: "approvedAmount",
            label: "Sanctioned Amount",
            style: { textAlign: "center" as any },
          },
          {
            field: "tenureInMonths",
            label: "Tenure",
            // INFO: `as any` is to resolve the TS issue
            // TODO: Figure out proper fix
            style: { textAlign: "center" as any },
          },
        ]
      : [
          {
            field: "maxAmount",
            label: "Sanctioned Amount",
            style: { textAlign: "center" as any },
          },
        ]),
    { field: "status", label: "Status", style: { textAlign: "center" as any } },
  ];
}

type SearchQueryType = {
  loanType: LoanType;
  pageNo: number;
  searchText: string;
  numberOfEntitiesPerPage: number;
  status: string[];
};

const DEFAULT_SEARCH_QUERY_STATE: SearchQueryType = {
  loanType: "TERM_LOAN",
  pageNo: 1,
  searchText: "",
  numberOfEntitiesPerPage: 10,
  status: [],
};

export default function LoanScreen() {
  useScreenName("Personal Loans");
  const storeDispatch = useDispatch();

  const loadingQueue = useSelector(
    (state: ReduxState) => state.loan.loadingQueue,
  );
  const loans: TermLoanDetails[] | RetailODDetails[] | CommercialLoanDetails[] =
    useSelector((state: ReduxState) => state.loan.loans);
  const loanSearchFilters: Record<LoanType, SearchFilter[]> = useSelector(
    (state: ReduxState) => state.loan.searchFilters,
  );

  const [
    { loanType, pageNo, searchText, numberOfEntitiesPerPage, status },
    setSearchQuery,
  ] = useQueryParams<SearchQueryType>(DEFAULT_SEARCH_QUERY_STATE);

  const filterOptions = useMemo(() => {
    const filters = loanSearchFilters[loanType];
    if (!Array.isArray(filters)) return [];
    return filters.reduce(
      (accum, group) => [
        ...accum,
        ...group.values.map((value) => ({
          group: group.name,
          value,
          label: splitKeyWithSpace(value),
        })),
      ],
      [] as MultiSelectOption[],
    );
  }, [loanSearchFilters, loanType]);

  useEffect(() => {
    const filters = loanSearchFilters[loanType];
    if (!Array.isArray(filters)) return;
    if (filters.length > 0) return;
    dispatch(storeDispatch, getLoanSearchFilters(loanType));
  }, [loanType]);

  useEffect(() => {
    dispatch(
      storeDispatch,
      getLoansBySearch(
        clearFormData({
          numberOfEntitiesPerPage,
          pageNo,
          searchText,
          loanType,
          status,
        }),
      ),
    );
  }, [pageNo, searchText, loanType, numberOfEntitiesPerPage, status]);

  function handleSearch(searchText: string) {
    setSearchQuery((prevState) => ({ ...prevState, searchText, pageNo: 1 }));
  }

  function handleFilterChanges(options: MultiSelectOption[]) {
    const filterObject = convertFilterArrayToObject(options);
    setSearchQuery((prevState) => ({
      ...prevState,
      ...{ status: [] },
      ...filterObject,
      pageNo: 1,
    }));
  }

  function getIdLink(path: RoutePaths, id?: string) {
    if (!id) return;
    const navigationLink = `${path}/${id}`;
    return (
      <Link to={navigationLink} className="table-link">
        {id}
      </Link>
    );
  }

  function getEmployeeNameAsLink(userId?: string, userName?: string) {
    if (!userName || !userId) return "-";
    return (
      <Link to={`users/individual/${userId}`} className="table-link">
        {userName}
      </Link>
    );
  }

  function getStatusBadge(status: LoanStatusEnum) {
    if ([LoanStatusEnum.DISBURSED, LoanStatusEnum.APPROVED].includes(status))
      return <Badge type="success">{status}</Badge>;
    if (status === LoanStatusEnum.OVERDUE)
      return <Badge type="reject">{status}</Badge>;
    return <Badge type="pending">{splitKeyWithSpace(status)}</Badge>;
  }

  function getTableData(
    loans: (TermLoanDetails | RetailODDetails | CommercialLoanDetails)[],
  ) {
    return loans.map(({ _id, createdAt, appId, irpa, pf, status, ...rest }) => {
      let user = undefined;
      let userId = undefined;
      let enterpriseId = undefined;
      let entitySnapshot = undefined;
      let approvedAmount = undefined;
      let moratoriumInMonths = undefined;
      let maxAmount = undefined;
      let tenor = undefined;
      if ("entitySnapshot" in rest) {
        entitySnapshot = rest.entitySnapshot;
      }
      if ("user" in rest) {
        user = rest.user;
      }
      if ("userId" in rest) {
        userId = rest.userId;
      }
      if ("enterpriseId" in rest) {
        enterpriseId = rest.enterpriseId;
      }
      if ("approvedAmount" in rest) {
        approvedAmount = rest.approvedAmount;
      }
      if ("moratoriumInMonths" in rest) {
        moratoriumInMonths = rest.moratoriumInMonths;
      }
      if ("maxAmount" in rest) {
        maxAmount = rest.maxAmount;
      }

      return {
        _id: getIdLink(RoutePaths.PERSONAL_LOANS, _id),
        userName: getEmployeeNameAsLink(user?._id, user?.employeeName),
        enterpriseName: splitKeyWithSpace(entitySnapshot?.snapshot?.name),
        // INFO: Temporarily hide employer name column on the loans list view in LMS Portal
        // For More details refer: https://refynetech.atlassian.net/browse/BANKING-286
        // employerName: user?.employerName ?? "-",
        createdAt: createdAt ? dateToDDMMYYYY(createdAt) : "-",
        appId: getIdLink(RoutePaths.APPLICATIONS, appId),
        userId: enterpriseId
          ? getIdLink(RoutePaths.ENTERPRISE_USERS, enterpriseId)
          : getIdLink(RoutePaths.INDIVIDUAL_USERS, userId),
        irpa,
        pf,
        // INFO: we don't have tenor filed in backend until so hardcoding in frontend
        tenor: 12,
        tenureInMonths: (rest as TermLoanDetails).tenureInMonths,
        approvedAmount,
        moratoriumInMonths,
        maxAmount,
        status: getStatusBadge(status),
      };
    });
  }

  return (
    <>
      <div className="dashboard-container p-3">
        <Tabs
          items={tabItems}
          value={loanType}
          disable={isLoadingActive(loadingQueue)}
          onChange={(value) => {
            setSearchQuery({
              ...DEFAULT_SEARCH_QUERY_STATE,
              loanType: value,
            });
          }}
        />
      </div>
      <div className="dashboard-container">
        <div className="d-flex flex-lg-row flex-column align-items-lg-end mb-4">
          <div className="search-input-user mr-lg-3 mb-4 mb-lg-0">
            <MultiSelect
              label="Filter"
              options={filterOptions}
              groupped
              value={convertFilterObjectToArray({ status })}
              onChange={handleFilterChanges}
            />
          </div>
          <SearchBox
            value={searchText}
            label="Search"
            placeholder="Search by User Name"
            onSubmit={handleSearch}
          />
        </div>

        <Table
          loading={isLoadingActive(loadingQueue)}
          columns={getColumns(loanType)}
          data={getTableData(loans)}
        />
      </div>
      <Pagination
        currentPage={pageNo}
        hasNextPage={loans.length >= numberOfEntitiesPerPage}
        numberOfEntriesPerPage={numberOfEntitiesPerPage}
        onPageChange={(pageNo) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            pageNo,
          }))
        }
        onPerPageCountChange={(countPerPage) =>
          setSearchQuery((prevState) => ({
            ...prevState,
            numberOfEntitiesPerPage: countPerPage,
            pageNo: 1,
          }))
        }
      />
    </>
  );
}
